<script setup lang="ts">
import { FileWithBlob, useFilesStore } from "@/stores/files";
import FileEntry from "./FileEntry.vue";
import { useSorted } from "@vueuse/core";
import { computed, ref } from "vue";

const fileStore = useFilesStore();

const getFilePoints = (file: FileWithBlob) => {
    let points = 0;
    if (file.isUploaded) points -= 1;
    if (file.isDuplicate) points += 2;
    if (file.isForbidden) points += 4;
    if (file.hasError) points += 8;
    return -points;
};

const filterOptions = [
    { value: "all", text: "All files" },
    { value: "uploaded", text: "Uploaded" },
    { value: "duplicates", text: "Duplicates" },
    { value: "errors", text: "Errors" },
    { value: "not-uploaded", text: "Not uploaded" },
    { value: "detached", text: "Detached" },
] as const;

const filterMode = ref<(typeof filterOptions)[number]["value"]>("all");
const counts = computed(() => {
    return fileStore.fullFiles.reduce(
        (acc, file) => {
            if (file.isDuplicate) acc.duplicates += 1;
            else if (file.hasError) acc.errors += 1;
            else if (file.isUploaded) acc.uploaded += 1;
            else if (!file.attachedFile) acc.detached += 1;
            else acc.pending += 1;
            return acc;
        },
        { duplicates: 0, errors: 0, uploaded: 0, detached: 0, pending: 0 }
    );
});

const filterFiles = (files: FileWithBlob[]) => {
    const filters: Record<string, (file: FileWithBlob) => boolean> = {
        all: () => true,
        uploaded: (file) => file.isUploaded,
        duplicates: (file) => file.isDuplicate,
        errors: (file) => file.hasError,
        "not-uploaded": (file) => !file.isUploaded,
        detached: (file) => !file.attachedFile && !file.isUploaded && !file.isDuplicate,
    };
    return files.filter(filters[filterMode.value]);
};

const sortedFiles = useSorted(
    () => filterFiles(fileStore.fullFiles),
    (a, b) => {
        const aPoints = getFilePoints(a);
        const bPoints = getFilePoints(b);
        if (aPoints === bPoints) return a.fileName.localeCompare(b.fileName);
        return Math.sign(aPoints - bPoints);
    }
);
</script>

<template>
    <div class="file-list-container">
        <div class="file-list-header">
            <span>filename</span>
            <span>Size</span>
            <select class="filter" v-model="filterMode">
                <option v-for="option in filterOptions" :value="option.value">{{ option.text }}</option>
            </select>
        </div>
        <div class="file-list">
            <div v-for="[file, progress] in fileStore.hashingTasks" class="hasing-task">
                <span>{{ file.name }}</span>
                <span>{{ progress.toFixed(2) }}% </span>
                <span>HASHING</span>
            </div>
            <FileEntry v-for="file in sortedFiles" :file :key="file.id" />
        </div>
        <div class="file-list-footer">
            <span>
                <span>total:</span>
                <span>{{ fileStore.fullFiles.length }}</span>
            </span>
            <span data-ok>
                <span>pending:</span>
                <span>{{ counts.pending }}</span>
            </span>
            <span data-error>
                <span>detached:</span>
                <span>{{ counts.detached }}</span>
            </span>
            <span data-ok>
                <span>uploaded:</span>
                <span>{{ counts.uploaded }}</span>
            </span>
            <span data-error>
                <span>errors:</span>
                <span>{{ counts.errors }}</span>
            </span>
            <span data-warn>
                <span>duplicates:</span>
                <span>{{ counts.duplicates }}</span>
            </span>
        </div>
    </div>
</template>

<style scoped lang="scss">
.file-list-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    overflow: auto;

    .file-list-header {
        display: grid;
        grid-template-columns: 4fr 120px 120px;
        align-items: center;
        font-size: 14px;

        span {
            padding: 0 5px;
            &:nth-child(2) {
                text-align: center;
            }
        }
    }

    .filter {
        width: 100%;
        padding: 5px;
        background-color: #ffffff;
        outline: none;
        border: 1px solid #000000;
        font-size: 14px;
    }
}

.file-list {
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: auto;
    border: 1px solid #727272;
    height: 100%;
    .hasing-task {
        display: grid;
        grid-template-columns: 1fr 140px 100px;
        align-items: center;
        padding: 10px;
        border-bottom: 1px solid #727272;

        span {
            font-size: 14px;
            &:nth-child(2) {
                text-align: center;
            }
            &:nth-child(3) {
                text-align: right;
            }
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
}

.file-list-footer {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    width: 100%;

    gap: 10px;

    & > span {
        font-size: 14px;

        width: 100px;

        display: flex;
        justify-content: space-between;

        &[data-error] {
            color: #dd300e;
        }
        &[data-warn] {
            color: #de780b;
        }
        &[data-ok] {
            color: #389b0a;
        }
    }

    & > hr {
        border: 1px solid #727272;
        height: 100%;
    }
}
</style>
